import { useCallback } from 'react';
import { trim } from 'lodash';

import { useSelectNewProjectTypePopup } from 'src/components/SelectNewProjectTypePopup';
import { useTemplateFoundPopup } from 'src/components/TemplateFoundPopup';
import { GENERATION_TOOL, isFreestyleGenerationTool } from 'src/data/generationTool';
import { isLongFormProjectType, PROJECT_TYPE } from 'src/data/project';
import * as ls from 'src/lib/localData';
import { useCreateProject } from 'src/services/useCreateProject';
import { useErrorHandler } from 'src/services/useErrorHandler';
import { useProjectTemplatePopupSubmitHandler } from 'src/services/useProjectTemplatePopupSubmitHandler';
import { useNotificationActions } from 'src/store/notification';
import {
  PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB,
  useProjectTemplatePopup,
} from 'src/views/ProjectTemplatePopup';

import { useEvents } from './services/useEvents';
import { CLASSIFIED_TEMPLATE_BLOG, WIDGET_ACTION } from './lib';
import { useMainActions } from './store';

export const useHandlers = ({ onFlowFinished, inPopupMode }) => {
  const createProject = useCreateProject();
  const openSelectNewProjectTypePopup = useSelectNewProjectTypePopup();
  const requestClassifiedToolUserApproval = useRequestClassifiedToolUserApproval();
  const openProjectTemplatePopup = useProjectTemplatePopup();
  const handleProjectTemplatePopupSubmit = useProjectTemplatePopupSubmitHandler();
  const handleError = useErrorHandler();
  const events = useEvents({ inPopupMode });

  const notifications = useNotificationActions();
  const mainActions = useMainActions();

  const handlePromptChange = useCallback(
    (newValue, newData) => {
      mainActions.updatePrompt(newValue, newData);
    },
    [mainActions]
  );

  const handleEditorChange = useCallback(
    (newValue) => {
      mainActions.updateEditorType(newValue);
    },
    [mainActions]
  );

  const handleWidgetAction = useCallback(
    async (action) => {
      switch (action) {
        /**
         * Ask user if to open a new "short form" or "long form" content and redirect to general prompt / blank template
         */
        case WIDGET_ACTION.createBlankProject: {
          events.trackQuickNavigation('new_blank_project');

          const params = inPopupMode
            ? {
                title: 'New blank project',
                description: 'Select the editor to use with this project:',
                displayBackButton: true,
              }
            : { narrow: true };

          const result = await openSelectNewProjectTypePopup(params);
          if (!result) {
            return;
          }

          events.trackBlankProject(result.projectType);
          createProject(result.projectType, {
            generationTool: isLongFormProjectType(result.projectType)
              ? GENERATION_TOOL.longFormBlank
              : GENERATION_TOOL.freestyle,
          });
          onFlowFinished?.();
          break;
        }

        /**
         * Ask user if to open a new "short form" or "long form" content and redirect to relevant "content improver" tools there
         */
        case WIDGET_ACTION.improveContent: {
          events.trackQuickNavigation('improve_existing_content');

          const params = inPopupMode
            ? {
                title: 'New blank project',
                description: 'Select the editor to use with this project:',
                displayBackButton: true,
              }
            : { narrow: true };

          const result = await openSelectNewProjectTypePopup(params);
          if (!result) {
            return;
          }

          createProject(result.projectType, {
            generationTool: isLongFormProjectType(result.projectType)
              ? GENERATION_TOOL.longFormContentImprover
              : GENERATION_TOOL.basicContentImprover,
          });
          onFlowFinished?.();
          break;
        }

        /**
         * Open the template gallery on default tab (”Most popular”)
         */
        case WIDGET_ACTION.openTemplateGallery: {
          events.trackQuickNavigation('template_gallery');

          openProjectTemplatePopup({
            selectedTabType: PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB.mostPopular,
            onSubmit: (...args) => {
              handleProjectTemplatePopupSubmit(...args);
              onFlowFinished?.();
            },
          });
          break;
        }

        /**
         * Open the template gallery on “Your saved templates” tab
         */
        case WIDGET_ACTION.openSavedTemplates: {
          events.trackQuickNavigation('saved_templates');

          openProjectTemplatePopup({
            selectedTabType: PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB.savedTemplates,
            onSubmit: (...args) => {
              handleProjectTemplatePopupSubmit(...args);
              onFlowFinished?.();
            },
          });
          break;
        }

        /**
         * Handle "new project prompt" submission
         */
        case WIDGET_ACTION.submit: {
          events.trackSubmit(mainActions.getPromptText());

          const promptData = mainActions.getPromptData();
          if (!promptData || trim(promptData?.text) === '') {
            notifications.displayWarning('Cannot classify empty prompt');
            return;
          }

          try {
            const classifiedTool = await mainActions.classifyTemplate();

            // If the classified tool is "long form blank" - create a long form project
            if (classifiedTool === GENERATION_TOOL.longFormBlank) {
              createProject(PROJECT_TYPE.longFormBuilder, {
                generationTool: GENERATION_TOOL.longFormBlank,
                promptData: mainActions.getPromptData(),
              });
              onFlowFinished?.();
              return;
            }

            const approvalResult = await requestClassifiedToolUserApproval({
              classifiedTool,
              inPopupMode,
            });
            // If user closed/backed the popup - return to the "widget"
            if (!approvalResult) {
              return;
            }

            // Based on the user's choice, create a new project of specific type
            if (approvalResult === GENERATION_TOOL.blogDescription) {
              createProject(PROJECT_TYPE.blogPostBuilder, {
                promptData: mainActions.getPromptData(),
              });
            } else if (approvalResult === GENERATION_TOOL.longFormBlank) {
              createProject(PROJECT_TYPE.longFormBuilder, {
                generationTool: GENERATION_TOOL.longFormBlank,
                promptData: mainActions.getPromptData(),
              });
            } else {
              createProject(PROJECT_TYPE.document, {
                generationTool: approvalResult,
                promptData: mainActions.getPromptData(),
              });
            }
            onFlowFinished?.();
          } catch (error) {
            handleError(error);
          }
          break;
        }
      }
    },
    [
      createProject,
      events,
      handleError,
      handleProjectTemplatePopupSubmit,
      inPopupMode,
      mainActions,
      notifications,
      onFlowFinished,
      openProjectTemplatePopup,
      openSelectNewProjectTypePopup,
      requestClassifiedToolUserApproval,
    ]
  );

  return {
    handlePromptChange,
    handleWidgetAction,
    handleEditorChange,
  };
};

const useRequestClassifiedToolUserApproval = () => {
  const openTemplateFoundPopup = useTemplateFoundPopup();

  const alignTool = (tool) => {
    // BE can return "blog" as a classified tool - we will handle it as "blogDescription" in FE logic
    if (tool === CLASSIFIED_TEMPLATE_BLOG) {
      return GENERATION_TOOL.blogDescription;
    }
    return tool;
  };

  return useCallback(
    async ({ classifiedTool, inPopupMode }) => {
      const alignedTool = alignTool(classifiedTool);

      // If user has already chosen to not show the popup, return the "classified" tool
      if (ls.templateFoundPopupDoNotShowAgain.read()) {
        return alignedTool;
      }

      // If the tool is "freestyle", no need to ask for user's approval
      if (isFreestyleGenerationTool(alignedTool)) {
        return alignedTool;
      }

      // Request user's approval for the classified tool
      const result = await openTemplateFoundPopup({
        generationTool: alignedTool,
        displayBackButton: inPopupMode,
      });
      if (!result) {
        return null;
      }

      // Save user's choice
      if (result.dontShowAgain) {
        ls.templateFoundPopupDoNotShowAgain.write(true);
      }

      const defaultTool =
        alignedTool === GENERATION_TOOL.blogDescription
          ? GENERATION_TOOL.longFormBlank
          : GENERATION_TOOL.freestyle;
      return result.generationTool || defaultTool;
    },
    [openTemplateFoundPopup]
  );
};
